import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Modal, Spinner, Badge, Alert, Container, Row, Col } from "react-bootstrap";
import { FaPlus, FaEdit, FaBan } from "react-icons/fa";
import PlantillaForm from "./PlantillaForm";
import { getPlantillasProcedimiento, patchDesactivarPlantilla } from "../../../api/common";

// Lista de plantillas de procedimiento
const PlantillasList = () => {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toDeactivate, setToDeactivate] = useState(null);
  const [alert, setAlert] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlantillasProcedimiento();
      // `request` devuelve el objeto axios; los datos útiles suelen estar en res.data.data
      const payload = res && res.data ? (res.data.data ?? res.data) : res;
      // Aseguramos que plantillas sea un array para evitar errors en .map
      const list = Array.isArray(payload) ? payload : (payload && Array.isArray(payload.items) ? payload.items : []);
      setPlantillas(list);
    } catch (err) {
      setError(err.message || "Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleEdit = (p) => {
    setSelected(p);
    setShowForm(true);
  };

  const handleNew = () => {
    setSelected(null);
    setShowForm(true);
  };

  const handleDeactivate = (p) => {
    setToDeactivate(p);
    setShowConfirm(true);
  };

  const confirmDeactivate = async () => {
    if (!toDeactivate) return;
    try {
      await patchDesactivarPlantilla(toDeactivate.id || toDeactivate._id);
      setAlert({ variant: "success", msg: "Plantilla desactivada" });
      setShowConfirm(false);
      load();
    } catch (err) {
      setAlert({ variant: "danger", msg: err.message || "Error al desactivar" });
    }
  };

  return (
    <Container fluid>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="bg-primary text-white p-2">Plantillas de Procedimiento</h4>
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={handleNew}>
            <FaPlus /> Nueva Plantilla
          </Button>
        </Col>
      </Row>

      {alert && (
        <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible>
          {alert.msg}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : plantillas.length === 0 ? (
        <Alert variant="info">No hay plantillas registradas.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Código</th>
              <th>Descripción</th>
              <th>Tipos de equipo</th>
              <th>Patrones requeridos</th>
              <th>Magnitudes</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {plantillas.map((p) => (
              <tr key={p.id || p._id || p.codigo}>
                <td>{p.codigo}</td>
                <td>{p.descripcion}</td>
                <td>{(p.tiposEquipoAplicable || []).join(", ")}</td>
                <td>{p.cantPatronesRequeridos}</td>
                <td>{(p.magnitudesConfig || []).map((m) => m.nombre).join(", ")}</td>
                <td>
                  <Badge bg={(p.estado === "activo" || p.activo) ? "success" : "secondary"}>
                    {(p.estado === "activo" || p.activo) ? "activo" : "inactivo"}
                  </Badge>
                </td>
                <td>
                  <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => handleEdit(p)}>
                    <FaEdit />
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDeactivate(p)}>
                    <FaBan />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selected ? "Editar Plantilla" : "Nueva Plantilla"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PlantillaForm
            plantilla={selected}
            onCancel={() => setShowForm(false)}
            onSuccess={() => {
              setShowForm(false);
              setAlert({ variant: "success", msg: "Plantilla guardada" });
              load();
            }}
          />
        </Modal.Body>
      </Modal>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar desactivación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Desea desactivar la plantilla <strong>{toDeactivate?.codigo}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancelar</Button>
          <Button variant="danger" onClick={confirmDeactivate}>Desactivar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PlantillasList;
